import { useState } from "react";
import { motion } from "framer-motion";
import { MemberOfferBanner } from "@/components/MemberOfferBanner";
import { SocietyInvite } from "@/components/SocietyInvite";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { tierOf } from "@/lib/membership";
import { Check, Sparkles, Flame } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    key: "velluto_basic",
    label: "Basic",
    price: "$39.99",
    note: "AUD / month · 7-day free trial",
    highlight: false,
    perks: [
      "Unblurred photos across the club",
      "10 messages every day",
      "Advanced fetish & preference filters",
      "A Basic member badge",
    ],
  },
  {
    key: "velluto_premium",
    label: "Premium",
    price: "$59.99",
    note: "AUD / month · 7-day free trial",
    highlight: true,
    perks: [
      "Everything in Basic",
      "Unlimited messaging — no daily cap",
      "See everyone who admires you",
      "Incognito mode — browse invisibly",
      "Cam-to-cam with AI face masking",
      "Group events access",
      "Priority placement in Discover",
    ],
  },
];

export default function Premium() {
  const { user } = useAuth();
  const tier = tierOf(user);
  const [loading, setLoading] = useState("");

  const checkout = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout");
      setLoading("");
    }
  };

  return (
    <div className="pt-24 pb-24 max-w-5xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-center mb-16">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center justify-center gap-2"><Sparkles size={14} /> Velluto Premium</p>
        <h1 className="font-display text-6xl md:text-7xl font-light">Unlock the whole room</h1>
        <p className="mt-5 text-sm text-text-muted">Both tiers start with a <span className="text-gold">7-day free trial</span> — cancel anytime before it ends.</p>
        {tier === "premium" && <p data-testid="premium-active" className="mt-4 text-gold">You're a Premium member — enjoy everything.</p>}
        {tier === "basic" && <p data-testid="basic-active" className="mt-4 text-gold">You're on Basic — upgrade to Premium for the full experience.</p>}
      </motion.div>

      <MemberOfferBanner user={user} />
      <SocietyInvite />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
        className="max-w-3xl mx-auto mb-10 rounded-3xl border border-orange-400/30 bg-surface p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-full bg-orange-500/15 flex items-center justify-center"><Flame size={22} className="text-orange-400" /></span>
          <div>
            <p className="font-display text-3xl font-light leading-none">Profile Boost</p>
            <p className="text-sm text-text-muted mt-2">Jump to the top of Discover for a full hour — up to 10× more eyes on you. $4.99.</p>
          </div>
        </div>
        {user?.boosted ? (
          <span data-testid="boost-active" className="shrink-0 flex items-center gap-2 px-6 py-3 rounded-full border border-orange-400/40 text-orange-300 text-xs uppercase tracking-[0.15em]"><Flame size={14} /> Boost active</span>
        ) : (
          <button data-testid="checkout-profile_boost" disabled={loading === "profile_boost"} onClick={() => checkout("profile_boost")}
            className="shrink-0 px-8 py-4 rounded-full bg-orange-500 hover:bg-orange-600 transition-colors text-sm uppercase tracking-[0.2em] disabled:opacity-50">
            {loading === "profile_boost" ? "Redirecting…" : "Boost me"}
          </button>
        )}
      </motion.div>

      <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {PLANS.map((plan, i) => {
          const owned = plan.key === "velluto_basic" ? tier !== "free" : tier === "premium";
          return (
          <motion.div key={plan.key} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 * i }}
            className={`relative rounded-3xl border p-10 bg-surface overflow-hidden ${plan.highlight ? "border-gold/40" : "border-white/10"}`}>
            {plan.highlight && <span className="absolute top-6 right-6 text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/40 px-3 py-1 rounded-full">Most popular</span>}
            <p className="text-xs uppercase tracking-[0.2em] text-text-muted">{plan.label}</p>
            <p className="font-display text-6xl font-light mt-3">{plan.price}</p>
            <p className="text-sm text-text-muted mt-1">{plan.note}</p>
            <ul className="mt-8 space-y-3">
              {plan.perks.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-text-main/85">
                  <Check size={16} className="text-gold mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <button data-testid={`checkout-${plan.key}`} disabled={loading === plan.key || owned} onClick={() => checkout(plan.key)}
              className={`w-full mt-10 py-4 rounded-full text-sm uppercase tracking-[0.2em] transition-colors disabled:opacity-40 ${plan.highlight ? "bg-gold text-ink hover:bg-gold/90" : "bg-wine hover:bg-wine-hover"}`}>
              {owned ? "Active" : loading === plan.key ? "Redirecting…" : "Start free trial"}
            </button>
          </motion.div>
          );
        })}
      </div>
    </div>
  );
}
